import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { IafItemSvc, IafFile } from '../../services/platformApi';
import {
  AnomalyItem,
  AnomalyCollection,
  AnomalyFormData,
  AnomalyStatus,
  AnomalySummaryFile,
  AnomalyMetric,
} from './types';
import {
  ANOMALY_COLLECTION_NAME,
  ANOMALY_COLLECTION_USER_TYPE,
  ANOMALY_FILE_COLLECTION_NAME,
  ANOMALY_STATUSES,
  EQUIPMENT_TYPES,
  METRIC_PRESETS,
  getEmptyAnomalyForm,
  generateAnomalyId,
} from './constants';
import './AnomaliesManagementPage.css';

const AnomaliesManagementPage: React.FC = () => {
  const { accessToken } = useAuth();
  const { selectedProject } = useApp();

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Collection state
  const [anomalyCollection, setAnomalyCollection] = useState<AnomalyCollection | null>(null);
  const [fileCollection, setFileCollection] = useState<AnomalyCollection | null>(null);

  // Anomaly items
  const [anomalies, setAnomalies] = useState<AnomalyItem[]>([]);

  // Form state
  const [isCreating, setIsCreating] = useState(false);
  const [anomalyForm, setAnomalyForm] = useState<AnomalyFormData>(getEmptyAnomalyForm());

  // Filter state
  const [statusFilter, setStatusFilter] = useState<AnomalyStatus | 'ALL'>('ALL');

  // Get project namespace
  const getProjectNamespace = useCallback((): string[] => {
    const namespaces = selectedProject?._namespaces || [];
    return namespaces.length > 0 ? [namespaces[0]] : [];
  }, [selectedProject]);

  // Create context for API calls
  const getCtx = useCallback(() => {
    const namespaces = getProjectNamespace();
    return {
      authToken: accessToken || '',
      _namespaces: namespaces,
      project: selectedProject,
    };
  }, [accessToken, getProjectNamespace, selectedProject]);

  // Clear messages after delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // ============================================================================
  // COLLECTION MANAGEMENT
  // ============================================================================

  const fetchOrCreateAnomalyCollection = useCallback(async () => {
    if (!accessToken || !selectedProject) return null;

    const ctx = getCtx();
    const namespaces = getProjectNamespace();

    try {
      // Try to find existing collection
      const result = await IafItemSvc.getNamedUserItems({
        query: {
          _userType: ANOMALY_COLLECTION_USER_TYPE,
          _name: ANOMALY_COLLECTION_NAME,
        },
      }, ctx);

      if (result._list && result._list.length > 0) {
        console.log('Found existing anomaly collection:', result._list[0]);
        return result._list[0] as AnomalyCollection;
      }

      // Create new collection
      console.log('Creating anomaly collection...');
      const collectionData = {
        _name: ANOMALY_COLLECTION_NAME,
        _shortName: 'anomaly_records',
        _userType: ANOMALY_COLLECTION_USER_TYPE,
        _description: 'Collection for storing anomaly records for notification testing',
        _namespaces: namespaces,
      };

      console.log('Collection data:', JSON.stringify(collectionData, null, 2));

      // createNamedUserItems takes an array and returns an array
      const createResult = await IafItemSvc.createNamedUserItems(
        [collectionData],
        'NamedUserCollection',
        ctx
      );
      console.log('Created anomaly collection:', createResult);
      
      // Get the first item from the result array
      const created = Array.isArray(createResult) ? createResult[0] : createResult;
      
      // Ensure the returned object has _name (API might return without it)
      return {
        ...created,
        _name: created?._name || ANOMALY_COLLECTION_NAME,
      } as AnomalyCollection;
    } catch (err) {
      console.error('Error fetching/creating anomaly collection:', err);
      throw err;
    }
  }, [accessToken, selectedProject, getCtx, getProjectNamespace]);

  const fetchOrCreateFileCollection = useCallback(async () => {
    if (!accessToken || !selectedProject) return null;

    const ctx = getCtx();
    const namespaces = getProjectNamespace();

    try {
      // Try to find existing file container using getContainers
      const nsProvider = { _namespaces: namespaces };
      const filters = [
        { _name: ANOMALY_FILE_COLLECTION_NAME },
      ];
      
      const containers = await IafFile.getContainers(nsProvider, filters, ctx);
      console.log('Found containers:', containers);

      if (containers && containers.length > 0) {
        console.log('Found existing file container:', containers[0]);
        return containers[0] as AnomalyCollection;
      }

      // First, get or create the root container
      let rootContainer = await IafFile.getRootContainer(nsProvider, ctx);
      console.log('Root container:', rootContainer);
      
      if (!rootContainer) {
        console.log('Creating root container...');
        rootContainer = await IafFile.createRootContainer({ _namespaces: namespaces }, ctx);
        console.log('Created root container:', rootContainer);
      }

      // Now create our container as a child of the root
      console.log('Creating file container as child of root...');
      const containerInfo = {
        _name: ANOMALY_FILE_COLLECTION_NAME,
        _shortName: 'anomaly_files',
        _description: 'Container for anomaly summary files (triggers notifications)',
      };

      const createResult = await IafFile.createContainer(rootContainer, containerInfo, ctx);
      console.log('Created file container:', createResult);
      
      // Ensure the returned object has _name (API might return without it)
      return {
        ...createResult,
        _name: createResult?._name || ANOMALY_FILE_COLLECTION_NAME,
      } as AnomalyCollection;
    } catch (err) {
      console.error('Error fetching/creating file collection:', err);
      throw err;
    }
  }, [accessToken, selectedProject, getCtx, getProjectNamespace]);

  // ============================================================================
  // ANOMALY ITEMS MANAGEMENT
  // ============================================================================

  const fetchAnomalies = useCallback(async () => {
    if (!anomalyCollection?._userItemId) return;

    setIsLoading(true);
    const ctx = getCtx();

    try {
      const result = await IafItemSvc.getRelatedItems(
        anomalyCollection._userItemId,
        {},
        ctx,
        { page: { _pageSize: 500 } }
      );

      const items = (result._list || []) as unknown as AnomalyItem[];
      // Sort by timestamp descending (newest first)
      items.sort((a, b) => new Date(b.timestamp || b.reportedAt || '').getTime() - new Date(a.timestamp || a.reportedAt || '').getTime());
      setAnomalies(items);
      console.log('Fetched anomalies:', items.length);
    } catch (err) {
      console.error('Error fetching anomalies:', err);
      setError('Failed to fetch anomalies');
    } finally {
      setIsLoading(false);
    }
  }, [anomalyCollection, getCtx]);

  const createAnomaly = async () => {
    if (!anomalyCollection?._userItemId) {
      setError('Anomaly collection not initialized');
      return;
    }

    if (!anomalyForm.equipmentSourceId) {
      setError('Equipment Source ID is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();
      const anomalyId = generateAnomalyId();
      const now = new Date().toISOString();

      // Build anomalous metrics array
      const anomalousMetrics: AnomalyMetric[] = [
        {
          metric: anomalyForm.metricName,
          zScore: anomalyForm.zScore,
          direction: anomalyForm.direction,
          currentValue: anomalyForm.currentValue,
          expectedMin: anomalyForm.expectedMin,
          expectedMax: anomalyForm.expectedMax,
          unit: anomalyForm.unit,
        },
        ...anomalyForm.additionalMetrics,
      ];

      const anomalyData: AnomalyItem = {
        anomalyId,
        status: anomalyForm.status,
        equipmentSourceId: anomalyForm.equipmentSourceId,
        equipmentType: anomalyForm.equipmentType,
        timestamp: now,
        reportedAt: now,
        totalAnomalies: anomalousMetrics.length,
        anomalousMetrics,
        correlatedMetrics: anomalousMetrics.map(m => m.metric),
        // Primary metric for email template
        metricName: anomalyForm.metricName,
        currentValue: anomalyForm.currentValue,
        expectedMin: anomalyForm.expectedMin,
        expectedMax: anomalyForm.expectedMax,
        zScore: anomalyForm.zScore,
        direction: anomalyForm.direction,
        unit: anomalyForm.unit,
        sourceId: anomalyForm.equipmentSourceId, // Alias for template
      };

      console.log('Creating anomaly:', anomalyData);

      await IafItemSvc.createRelatedItemsBulk(
        anomalyCollection._userItemId,
        [anomalyData as unknown as Record<string, unknown>],
        ctx
      );

      setSuccessMessage(`Anomaly ${anomalyId} created successfully!`);
      setIsCreating(false);
      setAnomalyForm(getEmptyAnomalyForm());
      await fetchAnomalies();
    } catch (err) {
      console.error('Error creating anomaly:', err);
      setError(`Failed to create anomaly: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const updateAnomalyStatus = async (anomaly: AnomalyItem, newStatus: AnomalyStatus) => {
    if (!anomalyCollection?._userItemId) return;

    setIsLoading(true);
    const ctx = getCtx();

    try {
      await IafItemSvc.updateRelatedItems(
        anomalyCollection._userItemId,
        [{
          ...anomaly,
          status: newStatus,
          _id: anomaly._id,
        }],
        ctx
      );

      setSuccessMessage(`Anomaly status updated to ${newStatus}`);
      await fetchAnomalies();
    } catch (err) {
      console.error('Error updating anomaly:', err);
      setError('Failed to update anomaly status');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAnomaly = async (anomaly: AnomalyItem) => {
    if (!anomalyCollection?._userItemId) return;
    if (!confirm(`Delete anomaly ${anomaly.anomalyId}?`)) return;

    setIsLoading(true);
    const ctx = getCtx();

    try {
      if (!anomaly._id) {
        throw new Error('Anomaly ID is missing');
      }
      await IafItemSvc.deleteRelatedItems(
        anomalyCollection._userItemId,
        [anomaly._id],
        ctx
      );

      setSuccessMessage('Anomaly deleted');
      await fetchAnomalies();
    } catch (err) {
      console.error('Error deleting anomaly:', err);
      setError('Failed to delete anomaly');
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================================
  // FILE MANAGEMENT (TRIGGERS NOTIFICATIONS)
  // ============================================================================

  const createOrUpdateSummaryFile = async () => {
    if (!fileCollection) {
      setError('File collection not initialized');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();

      // Calculate counts by status
      const countByStatus: { [key in AnomalyStatus]?: number } = {};
      for (const status of ANOMALY_STATUSES) {
        countByStatus[status.value] = anomalies.filter(a => a.status === status.value).length;
      }

      // Get last anomaly
      const lastAnomaly = anomalies[0]; // Already sorted by timestamp desc

      const summaryData: AnomalySummaryFile = {
        generatedAt: new Date().toISOString(),
        countByStatus,
        totalCount: anomalies.length,
        lastAnomalyId: lastAnomaly?.anomalyId || null,
        lastAnomalyTimestamp: lastAnomaly?.timestamp || null,
      };

      // Also include the last anomaly details for the notification template
      const fileContent = {
        ...summaryData,
        lastAnomaly: lastAnomaly ? {
          anomalyId: lastAnomaly.anomalyId,
          equipmentType: lastAnomaly.equipmentType,
          equipmentSourceId: lastAnomaly.equipmentSourceId,
          sourceId: lastAnomaly.sourceId || lastAnomaly.equipmentSourceId,
          metricName: lastAnomaly.metricName,
          currentValue: lastAnomaly.currentValue,
          expectedMin: lastAnomaly.expectedMin,
          expectedMax: lastAnomaly.expectedMax,
          zScore: lastAnomaly.zScore,
          direction: lastAnomaly.direction,
          unit: lastAnomaly.unit,
          timestamp: lastAnomaly.timestamp,
          status: lastAnomaly.status,
        } : null,
      };

      const fileName = 'anomaly_summary.json';
      const fileContentStr = JSON.stringify(fileContent, null, 2);

      // Check if file already exists
      let existingFile = null;
      const collectionId = fileCollection._userItemId || fileCollection._id;
      if (!collectionId) {
        throw new Error('File collection ID is missing');
      }
      try {
        const filesResult = await IafFile.getRelatedFiles(
          collectionId,
          { query: { _name: fileName } },
          ctx
        );
        if (filesResult._list && filesResult._list.length > 0) {
          existingFile = filesResult._list[0];
        }
      } catch {
        console.log('No existing summary file found');
      }

      // Create a blob and file object
      const blob = new Blob([fileContentStr], { type: 'application/json' });
      const file = new File([blob], fileName, { type: 'application/json' });

      // Upload file to the container - this will create or update the file
      // Re-uploading with the same name will trigger ResourceCreated or ResourceUpdated event
      await IafFile.uploadFile(fileCollection, file, ['anomaly-summary'], ctx);

      if (existingFile) {
        setSuccessMessage('Summary file UPDATED! This should trigger a ResourceUpdated event.');
        console.log('Summary file updated - ResourceUpdated event should fire');
      } else {
        setSuccessMessage('Summary file CREATED! This should trigger a ResourceCreated event.');
        console.log('Summary file created - ResourceCreated event should fire');
      }

      console.log('File content:', fileContent);
    } catch (err) {
      console.error('Error creating/updating summary file:', err);
      setError(`Failed to create/update summary file: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  useEffect(() => {
    const initialize = async () => {
      if (!accessToken || !selectedProject) return;

      setIsLoading(true);
      try {
        const [anomalyColl, fileColl] = await Promise.all([
          fetchOrCreateAnomalyCollection(),
          fetchOrCreateFileCollection(),
        ]);

        setAnomalyCollection(anomalyColl);
        setFileCollection(fileColl);
      } catch (err) {
        console.error('Error initializing:', err);
        setError('Failed to initialize collections');
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, [accessToken, selectedProject, fetchOrCreateAnomalyCollection, fetchOrCreateFileCollection]);

  useEffect(() => {
    if (anomalyCollection) {
      fetchAnomalies();
    }
  }, [anomalyCollection, fetchAnomalies]);

  // ============================================================================
  // HELPERS
  // ============================================================================

  const handleEquipmentTypeChange = (type: string) => {
    setAnomalyForm(prev => {
      const presets = METRIC_PRESETS[type as keyof typeof METRIC_PRESETS];
      const firstMetric = presets?.[0];
      
      return {
        ...prev,
        equipmentType: type,
        metricName: firstMetric?.name || prev.metricName,
        unit: firstMetric?.unit || prev.unit,
        expectedMin: firstMetric?.min || prev.expectedMin,
        expectedMax: firstMetric?.max || prev.expectedMax,
      };
    });
  };

  const handleMetricChange = (metricName: string) => {
    const presets = METRIC_PRESETS[anomalyForm.equipmentType as keyof typeof METRIC_PRESETS];
    const metric = presets?.find(m => m.name === metricName);
    
    if (metric) {
      setAnomalyForm(prev => ({
        ...prev,
        metricName,
        unit: metric.unit,
        expectedMin: metric.min,
        expectedMax: metric.max,
        // Generate a value outside the range based on direction
        currentValue: prev.direction === 'high' 
          ? metric.max * 1.25 
          : metric.min * 0.75,
      }));
    }
  };

  const getStatusColor = (status: AnomalyStatus): string => {
    return ANOMALY_STATUSES.find(s => s.value === status)?.color || '#666';
  };

  const filteredAnomalies = statusFilter === 'ALL' 
    ? anomalies 
    : anomalies.filter(a => a.status === statusFilter);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!selectedProject) {
    return (
      <div className="anomalies-management-page">
        <div className="page-header">
          <h1><i className="fas fa-exclamation-triangle"></i> Anomalies Management</h1>
        </div>
        <div className="empty-state">
          <i className="fas fa-folder-open"></i>
          <p>Please select a project to manage anomalies</p>
        </div>
      </div>
    );
  }

  return (
    <div className="anomalies-management-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1><i className="fas fa-exclamation-triangle"></i> Anomalies Management</h1>
          <p className="page-description">
            Create and manage anomaly records for testing notification triggers.
            Use the "Publish Summary File" button to trigger notification events.
          </p>
        </div>
        <div className="header-actions">
          <button
            className="btn-primary"
            onClick={() => setIsCreating(true)}
            disabled={isLoading || !anomalyCollection}
          >
            <i className="fas fa-plus"></i>
            Create Anomaly
          </button>
          <button
            className="btn-accent"
            onClick={createOrUpdateSummaryFile}
            disabled={isLoading || !fileCollection || anomalies.length === 0}
            title="Creates or updates a JSON file to trigger notification events"
          >
            <i className="fas fa-file-export"></i>
            Publish Summary File
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="message error">
          <i className="fas fa-exclamation-circle"></i>
          {error}
          <button onClick={() => setError(null)}><i className="fas fa-times"></i></button>
        </div>
      )}
      {successMessage && (
        <div className="message success">
          <i className="fas fa-check-circle"></i>
          {successMessage}
          <button onClick={() => setSuccessMessage(null)}><i className="fas fa-times"></i></button>
        </div>
      )}

      {/* Collection Status */}
      <div className="collection-status">
        <div className="status-item">
          <span className="status-label">Anomaly Collection:</span>
          {anomalyCollection ? (
            <span className="status-value success">
              <i className="fas fa-check-circle"></i>
              {anomalyCollection._name || ANOMALY_COLLECTION_NAME}
            </span>
          ) : isLoading ? (
            <span className="status-value pending">
              <i className="fas fa-spinner fa-spin"></i>
              Initializing...
            </span>
          ) : (
            <span className="status-value error">
              <i className="fas fa-exclamation-circle"></i>
              Not initialized
            </span>
          )}
        </div>
        <div className="status-item">
          <span className="status-label">File Collection:</span>
          {fileCollection ? (
            <span className="status-value success">
              <i className="fas fa-check-circle"></i>
              {fileCollection._name || ANOMALY_FILE_COLLECTION_NAME}
            </span>
          ) : isLoading ? (
            <span className="status-value pending">
              <i className="fas fa-spinner fa-spin"></i>
              Initializing...
            </span>
          ) : (
            <span className="status-value error">
              <i className="fas fa-exclamation-circle"></i>
              Not initialized
            </span>
          )}
        </div>
        {/* Retry button when initialization failed */}
        {(!anomalyCollection || !fileCollection) && !isLoading && (
          <button
            className="btn-secondary retry-btn"
            onClick={async () => {
              setIsLoading(true);
              setError(null);
              try {
                const [anomalyColl, fileColl] = await Promise.all([
                  !anomalyCollection ? fetchOrCreateAnomalyCollection() : Promise.resolve(anomalyCollection),
                  !fileCollection ? fetchOrCreateFileCollection() : Promise.resolve(fileCollection),
                ]);
                setAnomalyCollection(anomalyColl);
                setFileCollection(fileColl);
                if (anomalyColl) {
                  setSuccessMessage('Collections initialized successfully!');
                }
              } catch (err) {
                console.error('Error initializing:', err);
                setError(`Failed to initialize: ${err instanceof Error ? err.message : 'Unknown error'}`);
              } finally {
                setIsLoading(false);
              }
            }}
          >
            <i className="fas fa-sync-alt"></i>
            Initialize Collections
          </button>
        )}
      </div>

      {/* Stats Summary */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-value">{anomalies.length}</div>
          <div className="stat-label">Total Anomalies</div>
        </div>
        {ANOMALY_STATUSES.map(status => (
          <div 
            key={status.value} 
            className={`stat-card ${status.value.toLowerCase()}`}
            style={{ borderColor: status.color }}
          >
            <div className="stat-value" style={{ color: status.color }}>
              {anomalies.filter(a => a.status === status.value).length}
            </div>
            <div className="stat-label">{status.label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="filter-bar">
        <label>Filter by Status:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as AnomalyStatus | 'ALL')}
        >
          <option value="ALL">All Statuses</option>
          {ANOMALY_STATUSES.map(status => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>
        <span className="filter-count">Showing {filteredAnomalies.length} of {anomalies.length}</span>
      </div>

      {/* Anomalies List */}
      <div className="anomalies-list">
        {isLoading && anomalies.length === 0 && (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            Loading anomalies...
          </div>
        )}

        {!isLoading && filteredAnomalies.length === 0 && (
          <div className="empty-state">
            <i className="fas fa-check-circle"></i>
            <p>No anomalies found</p>
            <button className="btn-primary" onClick={() => setIsCreating(true)}>
              <i className="fas fa-plus"></i>
              Create Test Anomaly
            </button>
          </div>
        )}

        {filteredAnomalies.map(anomaly => (
          <div key={anomaly._id || anomaly.anomalyId} className="anomaly-card">
            <div className="anomaly-header">
              <div className="anomaly-id">
                <span className="id-label">ID:</span>
                <span className="id-value">{String(anomaly.anomalyId || 'N/A')}</span>
              </div>
              <div 
                className="anomaly-status"
                style={{ backgroundColor: getStatusColor(anomaly.status) }}
              >
                {String(anomaly.status || 'UNKNOWN')}
              </div>
            </div>

            <div className="anomaly-body">
              <div className="anomaly-info">
                <div className="info-row">
                  <span className="info-label">Equipment:</span>
                  <span className="info-value">{String(anomaly.equipmentType || 'N/A')}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Source ID:</span>
                  <span className="info-value">{String(anomaly.equipmentSourceId || 'N/A')}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Timestamp:</span>
                  <span className="info-value">{anomaly.timestamp ? new Date(anomaly.timestamp).toLocaleString() : 'N/A'}</span>
                </div>
              </div>

              <div className="anomaly-metrics">
                <h4>Anomalous Metrics ({typeof anomaly.totalAnomalies === 'number' ? anomaly.totalAnomalies : 0})</h4>
                <div className="metrics-list">
                  {anomaly.anomalousMetrics?.map((metric, idx) => {
                    // Safely get values - API might return objects instead of primitives
                    const direction = typeof metric.direction === 'string' ? metric.direction : 'unknown';
                    const metricName = typeof metric.metric === 'string' ? metric.metric : String(metric.metric || 'N/A');
                    const unit = typeof metric.unit === 'string' ? metric.unit : '';
                    const currentValue = typeof metric.currentValue === 'number' 
                      ? metric.currentValue.toFixed(2) 
                      : (typeof metric.currentValue === 'string' ? metric.currentValue : 'N/A');
                    const zScore = typeof metric.zScore === 'number' 
                      ? metric.zScore.toFixed(2) 
                      : (typeof metric.zScore === 'string' ? metric.zScore : 'N/A');
                    
                    return (
                      <div key={idx} className={`metric-item ${direction}`}>
                        <span className="metric-name">{metricName}</span>
                        <span className="metric-value">
                          {currentValue} {unit}
                        </span>
                        <span className="metric-zscore">
                          z={zScore} ({direction})
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="anomaly-actions">
              <select
                value={anomaly.status}
                onChange={(e) => updateAnomalyStatus(anomaly, e.target.value as AnomalyStatus)}
                className="status-select"
              >
                {ANOMALY_STATUSES.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
              <button
                className="btn-icon danger"
                onClick={() => deleteAnomaly(anomaly)}
                title="Delete anomaly"
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create Anomaly Modal */}
      {isCreating && (
        <div className="modal-overlay" onClick={() => setIsCreating(false)}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-plus-circle"></i>
                Create Test Anomaly
              </h2>
              <button className="btn-icon" onClick={() => setIsCreating(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Equipment Type*</label>
                  <select
                    value={anomalyForm.equipmentType}
                    onChange={(e) => handleEquipmentTypeChange(e.target.value)}
                  >
                    {EQUIPMENT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Equipment Source ID*</label>
                  <input
                    type="text"
                    value={anomalyForm.equipmentSourceId}
                    onChange={(e) => setAnomalyForm({ ...anomalyForm, equipmentSourceId: e.target.value })}
                    placeholder="e.g., CHILLER-001, AHU-002"
                  />
                </div>

                <div className="form-group">
                  <label>Initial Status</label>
                  <select
                    value={anomalyForm.status}
                    onChange={(e) => setAnomalyForm({ ...anomalyForm, status: e.target.value as AnomalyStatus })}
                  >
                    {ANOMALY_STATUSES.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Metric Name*</label>
                  <select
                    value={anomalyForm.metricName}
                    onChange={(e) => handleMetricChange(e.target.value)}
                  >
                    {METRIC_PRESETS[anomalyForm.equipmentType as keyof typeof METRIC_PRESETS]?.map(metric => (
                      <option key={metric.name} value={metric.name}>{metric.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Direction</label>
                  <select
                    value={anomalyForm.direction}
                    onChange={(e) => {
                      const direction = e.target.value as 'high' | 'low';
                      const currentValue = direction === 'high'
                        ? anomalyForm.expectedMax * 1.25
                        : anomalyForm.expectedMin * 0.75;
                      setAnomalyForm({ ...anomalyForm, direction, currentValue });
                    }}
                  >
                    <option value="high">High (above normal)</option>
                    <option value="low">Low (below normal)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Unit</label>
                  <input
                    type="text"
                    value={anomalyForm.unit}
                    onChange={(e) => setAnomalyForm({ ...anomalyForm, unit: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Current Value*</label>
                  <input
                    type="number"
                    step="0.01"
                    value={anomalyForm.currentValue}
                    onChange={(e) => setAnomalyForm({ ...anomalyForm, currentValue: parseFloat(e.target.value) })}
                  />
                </div>

                <div className="form-group">
                  <label>Expected Min</label>
                  <input
                    type="number"
                    step="0.01"
                    value={anomalyForm.expectedMin}
                    onChange={(e) => setAnomalyForm({ ...anomalyForm, expectedMin: parseFloat(e.target.value) })}
                  />
                </div>

                <div className="form-group">
                  <label>Expected Max</label>
                  <input
                    type="number"
                    step="0.01"
                    value={anomalyForm.expectedMax}
                    onChange={(e) => setAnomalyForm({ ...anomalyForm, expectedMax: parseFloat(e.target.value) })}
                  />
                </div>

                <div className="form-group">
                  <label>Z-Score</label>
                  <input
                    type="number"
                    step="0.1"
                    value={anomalyForm.zScore}
                    onChange={(e) => setAnomalyForm({ ...anomalyForm, zScore: parseFloat(e.target.value) })}
                  />
                  <span className="form-hint">Typically ≥ 2.0 for anomaly detection</span>
                </div>
              </div>

              <div className="form-preview">
                <h4><i className="fas fa-eye"></i> Preview (Email Template Variables)</h4>
                <div className="preview-content">
                  <code>
                    {JSON.stringify({
                      equipmentType: anomalyForm.equipmentType,
                      sourceId: anomalyForm.equipmentSourceId,
                      metricName: anomalyForm.metricName,
                      currentValue: anomalyForm.currentValue,
                      expectedMin: anomalyForm.expectedMin,
                      expectedMax: anomalyForm.expectedMax,
                      zScore: anomalyForm.zScore,
                      direction: anomalyForm.direction,
                      unit: anomalyForm.unit,
                    }, null, 2)}
                  </code>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => { setIsCreating(false); setAnomalyForm(getEmptyAnomalyForm()); }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={createAnomaly}
                disabled={isLoading || !anomalyForm.equipmentSourceId}
              >
                {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-plus"></i>}
                Create Anomaly
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnomaliesManagementPage;
